/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';
import Swal from 'sweetalert2';

// @mui material components
import Card from '@mui/material/Card';
import Switch from '@mui/material/Switch';
import Icon from '@mui/material/Icon';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';
import Fab from '@mui/material/Fab';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import i18n from 'components/Translation/Translate';

// Authentication layout components
import { APP_STATE } from 'services/Define';
import * as Defi from 'services/Define';
import {
  setHome,
  setToken,
  setClient,
  setPos,
  setUser,
  setLang,
  setPerm,
  setMenu,
  setAuthen,
  setDeviceNotify,
  clearState,
} from 'redux/actions/appAction';
import { PostData } from 'services/Request';
import * as Func from 'services/Function';

// Layout
import BasicLayout from './components/BasicLayout';
import bgImage from '../../assets/images/background-login.jpg';
import './SignInStyle.css';

const initAttr = {
  username: '',
  password: '',
  remember: false,
};

const initVerify = {
  username: false,
  password: false,
};

function SignIn() {
  const mod = 'authen';
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const app = useSelector((state) => state[Defi.APP_STATE]);
  const [attr, setAttr] = useState(initAttr);
  const [verify, setVerify] = useState(initVerify);

  const handleRemember = () => {
    if (attr.remember) {
      setAttr(initAttr);
      dispatch(setAuthen(initAttr));
    } else {
      setAttr({ ...attr, remember: true });
    }
  };

  const checkVerify = () => {
    if (!Func.verifyLength(attr.username, 5)) {
      setVerify({ ...verify, username: true });
      return false;
    }

    if (!Func.verifyLength(attr.password, 5)) {
      setVerify({ username: false, password: true });
      return false;
    }

    setVerify(initVerify);
    return true;
  };

  const checkBrowser = () => {
    const ua = window.navigator.userAgent;
    let tem;
    let M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
    if (/trident/i.test(M[1])) {
      tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
      return 'IE '.concat(tem[1] || '');
    }
    if (M[1] === 'Chrome') {
      tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
      if (tem != null) return tem.slice(1).join(' ').replace('OPR', 'Opera');
    }
    M = M[2] ? [M[1], M[2]] : [window.navigator.appName, window.navigator.appVersion, '-?'];
    tem = ua.match(/version\/(\d+)/i);
    if (tem != null) M.splice(1, 1, tem[1]);
    return M.join(' ');
  };

  const signIn = (event) => {
    event.preventDefault();

    if (!checkVerify()) return;

    PostData(`/login`, {
      lang: app.lang,
      client: app.client,
      browser: checkBrowser(),
      platform: window.navigator.platform,
      username: attr.username,
      password: attr.password,
    }).then((result) => {
      if (result.status === 'ok') {
        dispatch(setHome(result.url));
        dispatch(setToken(result.token));
        dispatch(setUser(result.user));
        dispatch(setMenu(result.menu));
        dispatch(setPerm(result.perm));
        dispatch(setLang(result.user.language));
        dispatch(setDeviceNotify(result.user.device_notify));
        dispatch(setPos(result.pos));

        if (attr.remember) {
          dispatch(
            setAuthen({
              remember: true,
              username: attr.username,
              password: attr.password,
            })
          );
        } else {
          dispatch(setAuthen(initAttr));
        }

        window.location.replace(result.url);
      } else {
        setVerify({ ...attr, username: false, password: true });
      }
    });
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    if (app.client === '') {
      let clientId = localStorage.getItem(APP_STATE) || '';
      if (!clientId) {
        clientId = uuid();
        localStorage.setItem(APP_STATE, clientId);
      }

      dispatch(setClient(clientId));
    }

    if (app.authen.remember) {
      setAttr({
        username: app.authen.username,
        password: app.authen.password,
        remember: true,
      });
    } else {
      setAttr(initAttr);
    }
  }, []);

  return (
    <BasicLayout image={bgImage} sx={{ overflow: 'hidden', minHeight: 900 }}>
      <Card className='signin'>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox mt={1} mb={5} textAlign='center'>
            <MDTypography variant='h3' fontWeight='medium' color='white'>
              {t(`app.name`)}
            </MDTypography>
          </MDBox>
          <MDBox component='form' role='form' onSubmit={signIn}>
            <MDBox mb={2}>
              <MDInput
                name='user_name'
                type='text'
                fullWidth
                onChange={(e) => setAttr({ ...attr, username: e.target.value })}
                sx={{ input: { color: '#ffffff', fontSize: '15px' } }}
                value={attr.username}
                InputProps={{
                  maxLength: 50,
                  autoFocus: true,
                  startAdornment: (
                    <InputAdornment position='start'>
                      <MDTypography variant='body' color='white' sx={{ width: 65 }}>
                        {t(`${mod}.username`)} :
                      </MDTypography>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end'>
                      <MDTypography variant='body' color='white'>
                        <Icon>person</Icon>
                      </MDTypography>
                    </InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
              {verify.username ? (
                <MDTypography color='error' variant='caption'>
                  <Icon>priority_high</Icon> {t(`authen.user_incorrect`)}
                </MDTypography>
              ) : null}
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                name='user_pass'
                type='password'
                fullWidth
                onChange={(e) => setAttr({ ...attr, password: e.target.value })}
                sx={{ input: { color: '#ffffff', fontSize: '15px' } }}
                value={attr.password}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <MDTypography variant='body' color='white' sx={{ width: 65 }}>
                        {t(`${mod}.password`)} :
                      </MDTypography>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end'>
                      <MDTypography variant='body' color='white'>
                        <Icon>lock_open</Icon>
                      </MDTypography>
                    </InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
              {verify.password ? (
                <MDTypography color='error' variant='caption'>
                  <Icon>priority_high</Icon> {t(`authen.pass_incorrect`)}
                </MDTypography>
              ) : null}
            </MDBox>
            <MDBox display='flex' alignItems='center' ml={-1}>
              <FormControlLabel
                control={
                  <Switch
                    color='info'
                    tabIndex={-1}
                    checked={attr.remember}
                    onClick={handleRemember}
                  />
                }
                sx={{ cursor: 'pointer', userSelect: 'none', ml: 0 }}
                label={
                  <MDTypography variant='button' fontWeight='regular' color='white'>
                    {t(`${mod}.remember_me`)}
                  </MDTypography>
                }
              />
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton type='submit' variant='contained' color='light' fullWidth>
                <MDTypography variant='h5' color='dark'>
                  {t(`${mod}.signin`)} <Icon>login</Icon>
                </MDTypography>
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
      <Fab
        size='small'
        color='secondary'
        aria-label='client'
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
        }}
        onClick={() =>
          Swal.fire({
            title: 'Client ID',
            text: app.client,
            confirmButtonText: 'Copy',
            confirmButtonColor: '#3085d6',
          }).then((result) => {
            if (result.isConfirmed) {
              navigator.clipboard.writeText(app.client);
              Swal.fire({
                icon: 'success',
                title: 'Copy completed!!',
                showConfirmButton: false,
                timer: 1500,
              });
            }
          })
        }
      >
        <Icon>desktop_windows_outlined</Icon>
      </Fab>
    </BasicLayout>
  );
}

export default SignIn;
